/**
 * Export sweep locations + readers subcollections to CSV or XLSX (download in browser).
 */
import * as XLSX from 'xlsx';
import { SweepsFirestore } from '../services/sweepsFirestore';
import type { SweepLocation, SweepReader } from '../types/sweep';

async function sweepFolderNameMap(masterOwnerID: string): Promise<Map<string, string>> {
  const folders = await SweepsFirestore.listSweepFolders(masterOwnerID);
  const m = new Map<string, string>();
  for (const f of folders) {
    m.set(f.id, f.name);
  }
  return m;
}

function tsToStr(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'object' && v !== null && 'toDate' in v && typeof (v as { toDate: () => Date }).toDate === 'function') {
    try {
      return (v as { toDate: () => Date }).toDate().toISOString();
    } catch {
      /* fall through */
    }
  }
  if (v instanceof Date) return v.toISOString();
  return String(v);
}

function locationRow(
  loc: SweepLocation,
  folderNames?: Map<string, string>
): Record<string, string | number | boolean | undefined> {
  const fid = loc.sweepFolderId != null && String(loc.sweepFolderId).trim() ? String(loc.sweepFolderId) : '';
  return {
    id: loc.id,
    masterOwnerID: loc.masterOwnerID,
    name: loc.name,
    address: loc.address,
    teamNumber: loc.teamNumber,
    stopSequence: loc.stopSequence,
    addressLine1: loc.addressLine1,
    city: loc.city,
    state: loc.state,
    zip: loc.zip,
    latitude: loc.latitude,
    longitude: loc.longitude,
    knownInfo: loc.knownInfo,
    readerCount: loc.readerCount,
    sweepScheduledDate: loc.sweepScheduledDate ?? '',
    sweepFolderId: fid,
    sweepFolderName: fid && folderNames ? folderNames.get(fid) ?? '' : '',
    sweepCheckedOff: loc.sweepCheckedOff,
    sweepCheckedOffReason: loc.sweepCheckedOffReason ?? '',
    sweepCheckedOffNotes: loc.sweepCheckedOffNotes ?? '',
    importBatchId: loc.importBatchId,
    createdByOwnerID: loc.createdByOwnerID,
    createdAt: tsToStr(loc.createdAt),
    updatedAt: tsToStr(loc.updatedAt),
  };
}

export async function buildSweepsExportData(masterOwnerID: string): Promise<{
  locations: SweepLocation[];
  readersFlat: Array<SweepReader & { sweepLocationId: string }>;
  folderNames: Map<string, string>;
}> {
  const locations = await SweepsFirestore.listSweepLocations(masterOwnerID);
  const folderNames = await sweepFolderNameMap(masterOwnerID);
  const readersFlat: Array<SweepReader & { sweepLocationId: string }> = [];
  for (const loc of locations) {
    const readers = await SweepsFirestore.listSweepReaders(loc.id);
    for (const r of readers) {
      readersFlat.push({
        ...r,
        sweepLocationId: loc.id,
        createdAt: r.createdAt as unknown,
      } as SweepReader & { sweepLocationId: string });
    }
  }
  return { locations, readersFlat, folderNames };
}

/** Single CSV: location fields + readersJson column (full reader objects per site). */
export async function downloadSweepsCsv(masterOwnerID: string): Promise<void> {
  const { locations, readersFlat, folderNames } = await buildSweepsExportData(masterOwnerID);
  const bySweep = new Map<string, SweepReader[]>();
  for (const r of readersFlat) {
    const sid = r.sweepLocationId;
    if (!bySweep.has(sid)) bySweep.set(sid, []);
    bySweep.get(sid)!.push(r);
  }
  const rows = locations.map((loc) => ({
    ...locationRow(loc, folderNames),
    readersJson: JSON.stringify(bySweep.get(loc.id) ?? []),
  }));
  const sheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{ id: '' }]);
  const csv = XLSX.utils.sheet_to_csv(sheet);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `sweeps-export-${masterOwnerID.slice(0, 8)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadSweepsXlsx(masterOwnerID: string): Promise<void> {
  const { locations, readersFlat, folderNames } = await buildSweepsExportData(masterOwnerID);
  const locRows = locations.map((loc) => locationRow(loc, folderNames));
  const readRows = readersFlat.map((r) => ({
    sweepLocationId: r.sweepLocationId,
    readerId: r.id,
    label: r.label,
    notes: r.notes ?? '',
    createdAt: tsToStr(r.createdAt),
  }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(locRows.length ? locRows : [{ id: '' }]),
    'locations'
  );
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(readRows.length ? readRows : [{ sweepLocationId: '' }]),
    'readers'
  );
  XLSX.writeFile(wb, `sweeps-export-${masterOwnerID.slice(0, 8)}.xlsx`);
}
